import React from "react";
import {
  AbsoluteFill,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  CheckCircle2,
  ArrowRight,
  Search,
} from "lucide-react";

export const KyuStoreRaihanStyle: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Snappy Spring helper (Raihan Visuals high-speed ease)
  const sp = (delay: number, damping = 12, stiffness = 160) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1.0 },
    });

  // Color Palette
  const pureWhite = "#FFFFFF";
  const darkInk = "#0B0C10";
  const kyuPurple = "#5D3FD3";
  const emeraldSuccess = "#10B981";
  const mutedGray = "#64748B";

  // Scene Intervals (Total: 540 frames = 18s @ 30fps)
  // Scene 1: 0 - 85 (0.0s - 2.8s) -> Kinetic Question Hook
  // Scene 2: 85 - 175 (2.8s - 5.8s) -> Brand Reveal ("Meet Kyu Store")
  // Scene 3: 175 - 295 (5.8s - 9.8s) -> Flanked Hero Device ("Cukup ID" [HP] "0.8 Detik")
  // Scene 4: 295 - 415 (9.8s - 13.8s) -> Micro-UI Zoom (Search bar + Nickname Auto-detect)
  // Scene 5: 415 - 540 (13.8s - 18.0s) -> Clean Minimalist CTA Button

  // ==========================================
  // SCENE 1: Kinetic Question Hook
  // ==========================================
  const s1Spring = sp(5, 14, 150);
  const s1Scale = interpolate(s1Spring, [0, 1], [0.88, 1]);
  const s1Opacity = interpolate(s1Spring, [0, 1], [0, 1]);

  // ==========================================
  // SCENE 2: Brand Reveal ("Meet Kyu Store")
  // ==========================================
  const s2Frame = frame - 85;
  const s2LogoSpring = sp(90, 10, 170);
  const s2LogoScale = interpolate(s2LogoSpring, [0, 1], [0, 1]);
  const s2TextSpring = sp(105, 12, 140);
  const s2TextX = interpolate(s2TextSpring, [0, 1], [25, 0]);

  // ==========================================
  // SCENE 3: Hero Device Flanked
  // ==========================================
  const s3Frame = frame - 175;
  const s3PhoneSpring = sp(180, 14, 120);
  const s3PhoneY = interpolate(s3PhoneSpring, [0, 1], [100, 0]);
  const s3LeftText = sp(195, 12, 150);
  const s3RightText = sp(205, 12, 150);

  // ==========================================
  // SCENE 4: Micro-UI Interaction Focus
  // ==========================================
  const s4Frame = frame - 295;
  const s4InputSpring = sp(300, 14, 140);
  const s4CardSpring = sp(330, 12, 150);
  const cursorBlink = Math.floor(frame / 12) % 2 === 0;

  // ==========================================
  // SCENE 5: Clean Call To Action
  // ==========================================
  const s5Frame = frame - 415;
  const s5BtnSpring = sp(420, 10, 160);
  const s5BtnScale = interpolate(s5BtnSpring, [0, 1], [0.8, 1]);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: pureWhite,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* ==================================================== */}
      {/* SCENE 1: KINETIC QUESTION HOOK (Frames 0 - 85)       */}
      {/* ==================================================== */}
      {frame < 85 && (
        <AbsoluteFill
          style={{
            backgroundColor: pureWhite,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 80,
          }}
        >
          <div
            style={{
              opacity: s1Opacity,
              transform: `scale(${s1Scale})`,
              textAlign: "center",
            }}
          >
            <h1
              style={{
                fontSize: 76,
                fontWeight: 900,
                color: darkInk,
                letterSpacing: "-0.03em",
                lineHeight: 1.15,
                margin: 0,
              }}
            >
              Mau top-up game <br />
              <span style={{ color: kyuPurple }}>gak pake nunggu?</span>
            </h1>
          </div>
        </AbsoluteFill>
      )}

      {/* ==================================================== */}
      {/* SCENE 2: BRAND REVEAL ("Meet Kyu Store") (85 - 175) */}
      {/* ==================================================== */}
      {frame >= 85 && frame < 175 && (
        <AbsoluteFill
          style={{
            backgroundColor: pureWhite,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 20,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 24,
            }}
          >
            <span
              style={{
                fontSize: 68,
                fontWeight: 800,
                color: darkInk,
                letterSpacing: "-0.02em",
              }}
            >
              Meet
            </span>

            {/* Logo Morph/Scale */}
            <div
              style={{
                width: 90,
                height: 90,
                borderRadius: "50%",
                backgroundColor: kyuPurple,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 10px 30px rgba(93, 63, 211, 0.35)",
                transform: `scale(${s2LogoScale})`,
              }}
            >
              <Img
                src={staticFile("kyu-store-logo.png")}
                style={{
                  width: 58,
                  height: 58,
                  objectFit: "contain",
                }}
              />
            </div>

            <span
              style={{
                fontSize: 68,
                fontWeight: 800,
                color: kyuPurple,
                letterSpacing: "-0.02em",
                opacity: interpolate(s2TextSpring, [0, 1], [0, 1]),
                transform: `translateX(${s2TextX}px)`,
              }}
            >
              kyustore
            </span>
          </div>

          <p
            style={{
              fontSize: 24,
              fontWeight: 600,
              color: mutedGray,
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              marginTop: 10,
              opacity: interpolate(s2TextSpring, [0, 1], [0, 1]),
            }}
          >
            The Automated Top-Up Platform
          </p>
        </AbsoluteFill>
      )}

      {/* ==================================================== */}
      {/* SCENE 3: FLANKED HERO DEVICE (Frames 175 - 295)      */}
      {/* ==================================================== */}
      {frame >= 175 && frame < 295 && (
        <AbsoluteFill
          style={{
            backgroundColor: "#F8FAFC",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          {/* Subtle Ambient Radial Glow */}
          <div
            style={{
              position: "absolute",
              width: 800,
              height: 800,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(93, 63, 211, 0.12) 0%, transparent 70%)",
              filter: "blur(60px)",
              pointerEvents: "none",
            }}
          />

          {/* Smartphone Mockup with Real kyustore.com */}
          <div
            style={{
              width: 420,
              height: 820,
              backgroundColor: "#16181F",
              borderRadius: 48,
              border: "6px solid #0B0C10",
              boxShadow: "0 30px 70px -15px rgba(11, 12, 16, 0.25)",
              overflow: "hidden",
              position: "relative",
              transform: `translateY(${s3PhoneY}px)`,
              zIndex: 5,
            }}
          >
            {/* Dynamic Island Header */}
            <div
              style={{
                height: 38,
                backgroundColor: "#0B0C10",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div style={{ width: 100, height: 16, backgroundColor: "#20222B", borderRadius: 12 }} />
            </div>

            {/* Real Mobile Screenshot */}
            <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
              <Img
                src={staticFile("kyustore_real_mobile.png")}
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "cover",
                }}
              />
            </div>
          </div>

          {/* Flanked Text Left: "CUKUP ID" */}
          <div
            style={{
              position: "absolute",
              left: 40,
              top: "48%",
              transform: `translateY(-50%) translateX(${interpolate(s3LeftText, [0, 1], [-40, 0])}px)`,
              opacity: interpolate(s3LeftText, [0, 1], [0, 1]),
              textAlign: "left",
              zIndex: 10,
            }}
          >
            <div style={{ fontSize: 72, fontWeight: 900, color: darkInk, lineHeight: 0.95, letterSpacing: "-0.03em" }}>
              CUKUP <br />
              <span style={{ color: kyuPurple }}>USER ID</span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: mutedGray, marginTop: 10 }}>
              Tanpa Password
            </div>
          </div>

          {/* Flanked Text Right: "0.8 DETIK" */}
          <div
            style={{
              position: "absolute",
              right: 40,
              top: "48%",
              transform: `translateY(-50%) translateX(${interpolate(s3RightText, [0, 1], [40, 0])}px)`,
              opacity: interpolate(s3RightText, [0, 1], [0, 1]),
              textAlign: "right",
              zIndex: 10,
            }}
          >
            <div style={{ fontSize: 72, fontWeight: 900, color: darkInk, lineHeight: 0.95, letterSpacing: "-0.03em" }}>
              0.8 <br />
              <span style={{ color: emeraldSuccess }}>DETIK</span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: mutedGray, marginTop: 10 }}>
              Scan QRIS Instan
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* ==================================================== */}
      {/* SCENE 4: MICRO-UI INTERACTION FOCUS (295 - 415)      */}
      {/* ==================================================== */}
      {frame >= 295 && frame < 415 && (
        <AbsoluteFill
          style={{
            backgroundColor: pureWhite,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 80px",
          }}
        >
          {/* Section Title */}
          <div
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: kyuPurple,
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              marginBottom: 24,
            }}
          >
            VERIFIKASI SISTEM OTOMATIS
          </div>

          {/* Interactive Search / Input Pill (Raihan Visuals Style) */}
          <div
            style={{
              width: "100%",
              maxWidth: 720,
              height: 90,
              borderRadius: 45,
              backgroundColor: pureWhite,
              border: `2.5px solid ${darkInk}`,
              boxShadow: "0 20px 40px -10px rgba(0, 0, 0, 0.12)",
              display: "flex",
              alignItems: "center",
              padding: "0 32px",
              gap: 20,
              transform: `scale(${interpolate(s4InputSpring, [0, 1], [0.92, 1])})`,
              opacity: interpolate(s4InputSpring, [0, 1], [0, 1]),
            }}
          >
            <Search size={32} color={kyuPurple} />
            <div style={{ fontSize: 34, fontWeight: 700, color: darkInk, letterSpacing: "0.02em" }}>
              ID: 88492019 (2481)
              <span style={{ color: kyuPurple, opacity: cursorBlink ? 1 : 0, fontWeight: 400 }}>
                |
              </span>
            </div>
          </div>

          {/* Staggered Verification Result Card */}
          <div
            style={{
              width: "100%",
              maxWidth: 720,
              marginTop: 24,
              borderRadius: 28,
              backgroundColor: "#F8FAFC",
              border: "1.5px solid #E2E8F0",
              padding: "24px 32px",
              boxShadow: "0 15px 30px rgba(0, 0, 0, 0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transform: `translateY(${interpolate(s4CardSpring, [0, 1], [40, 0])}px)`,
              opacity: interpolate(s4CardSpring, [0, 1], [0, 1]),
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
              <div
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: "50%",
                  backgroundColor: "#ECFDF5",
                  border: `2px solid ${emeraldSuccess}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircle2 size={32} color={emeraldSuccess} />
              </div>
              <div>
                <div style={{ fontSize: 28, fontWeight: 900, color: darkInk }}>
                  ZURA (VALID)
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, color: mutedGray }}>
                  Akun Mobile Legends Terverifikasi
                </div>
              </div>
            </div>

            <span
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: emeraldSuccess,
                backgroundColor: "#ECFDF5",
                padding: "8px 16px",
                borderRadius: 12,
                border: "1px solid rgba(16, 185, 129, 0.3)",
              }}
            >
              ANTI-SALAH ID
            </span>
          </div>
        </AbsoluteFill>
      )}

      {/* ==================================================== */}
      {/* SCENE 5: CLEAN CALL TO ACTION (Frames 415 - 540)     */}
      {/* ==================================================== */}
      {frame >= 415 && (
        <AbsoluteFill
          style={{
            backgroundColor: kyuPurple,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 80,
          }}
        >
          {/* Logo Brand Icon */}
          <div
            style={{
              width: 100,
              height: 100,
              borderRadius: "50%",
              backgroundColor: pureWhite,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.25)",
              marginBottom: 36,
              transform: `scale(${s5BtnScale})`,
            }}
          >
            <Img
              src={staticFile("kyu-store-logo.png")}
              style={{
                width: 65,
                height: 65,
                objectFit: "contain",
              }}
            />
          </div>

          <h2
            style={{
              fontSize: 56,
              fontWeight: 900,
              color: pureWhite,
              textAlign: "center",
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              marginBottom: 40,
            }}
          >
            Siap coba sensasi <br />
            top-up serba instan?
          </h2>

          {/* The Raihan Visuals Signature Pill Button */}
          <div
            style={{
              height: 84,
              padding: "0 16px 0 36px",
              backgroundColor: pureWhite,
              borderRadius: 42,
              display: "flex",
              alignItems: "center",
              gap: 24,
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
              transform: `scale(${s5BtnScale})`,
            }}
          >
            <span
              style={{
                fontSize: 30,
                fontWeight: 900,
                color: kyuPurple,
                letterSpacing: "0.02em",
              }}
            >
              Order di kyustore.com
            </span>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: kyuPurple,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ArrowRight size={28} color={pureWhite} />
            </div>
          </div>
        </AbsoluteFill>
      )}
    </AbsoluteFill>
  );
};
